import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AgentStatusBadge } from "@/components/AgentStatusBadge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle, Circle, ArrowSquareOut, FileText, CreditCard, Storefront, EnvelopeSimple,
  CircleNotch,
} from "@phosphor-icons/react";

// Jotform / IRS URLs — keep in sync with backend server.py ONBOARDING_LINKS.
// All four point to the official production destinations the user verified.
const JOTFORM = {
  agreement: "https://form.jotform.com/besttimetravelandtours/travel-advisors-agreement--e-sign",
  bank_info: "https://form.jotform.com/besttimetravelandtours/bank-payment-information",
  supplier_access: "https://form.jotform.com/besttimetravelandtours/request-supplier-access",
};
const W9_FORM_URL = "https://www.irs.gov/pub/irs-pdf/fw9.pdf";

const PAYOUT_EMAIL = "info@besttimetravelandtours.com";

const ITEMS = [
  {
    key: "agreement_signed",
    title: "Travel Advisor Agreement",
    description: "E-sign the agreement on Jotform.",
    Icon: FileText,
    cta: { label: "Sign agreement", href: JOTFORM.agreement },
    selfAttest: true,
    attestLabel: "I've signed the agreement",
  },
  {
    key: "bank_info_submitted",
    title: "Bank / Payment Details",
    description: "Submit your payout info securely (admin-only visibility).",
    Icon: CreditCard,
    cta: { label: "Submit bank info", href: JOTFORM.bank_info },
    selfAttest: true,
    attestLabel: "I've submitted my bank info",
  },
  {
    key: "supplier_access_completed",
    title: "Supplier Access",
    description: "Request access to our preferred supplier portals.",
    Icon: Storefront,
    cta: { label: "Request access", href: JOTFORM.supplier_access },
    selfAttest: true,
    attestLabel: "I've submitted my supplier request",
  },
  {
    key: "payout_verification",
    title: "Payout Verification",
    description: null, // custom body below
    Icon: EnvelopeSimple,
    cta: { label: `Email ${PAYOUT_EMAIL}`, href: `mailto:${PAYOUT_EMAIL}?subject=Payout%20Verification%20Documents` },
    customBody: true,
    // Admin-gated — no self-attest here.
  },
];

function OnboardingTile({ item, done, onAttest, attesting }) {
  const Icon = item.Icon;
  const [opened, setOpened] = useState(false);
  const openCta = () => setOpened(true);

  return (
    <div
      className={`relative rounded-lg p-4 border transition-colors ${
        done ? "border-emerald-400/40 bg-emerald-500/5" : "border-white/10 bg-black/20 hover:border-[#D4AF37]/30"
      }`}
      data-testid={`onboarding-item-${item.key}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          {done
            ? <CheckCircle size={22} weight="fill" className="text-emerald-400" />
            : <Circle size={22} weight="duotone" className="text-white/30" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon size={14} weight="duotone" className={done ? "text-emerald-300" : "text-[#D4AF37]"} />
            <p className={`font-sans text-sm font-medium ${done ? "text-emerald-200" : "text-white"}`}>{item.title}</p>
          </div>

          {item.customBody ? (
            <div className="text-xs text-white/60 font-sans leading-relaxed space-y-1.5">
              <p>
                To receive commission payouts, please email your documents to:
                <br />
                <a
                  href={`mailto:${PAYOUT_EMAIL}?subject=Payout%20Verification%20Documents`}
                  className="text-[#D4AF37] hover:text-[#F3C853] transition-colors inline-flex items-center gap-1"
                  data-testid="payout-verification-email"
                >
                  📧 {PAYOUT_EMAIL}
                </a>
              </p>
              <div className="pt-1.5 pl-3 border-l border-[#D4AF37]/25 text-[11.5px]">
                <p className="text-white/80">
                  <span className="text-[#D4AF37]">U.S. Travel Agents:</span>{" "}
                  <a
                    href={W9_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-[#D4AF37]/40 underline-offset-2 hover:text-[#F3C853] transition-colors"
                    data-testid="onboarding-w9-link"
                  >
                    W-9 Form
                  </a>
                  &nbsp;·&nbsp; Valid ID
                </p>
                <p className="text-white/80 mt-0.5"><span className="text-[#D4AF37]">International Travel Agents:</span> Valid ID</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-white/55 font-sans leading-relaxed">{item.description}</p>
          )}

          {!done && item.cta && (
            <a
              href={item.cta.href}
              target={item.customBody ? undefined : "_blank"}
              rel={item.customBody ? undefined : "noopener noreferrer"}
              onClick={openCta}
              className="inline-flex items-center gap-1.5 mt-2 text-xs uppercase tracking-widest text-[#D4AF37] hover:text-[#F3C853] transition-colors font-sans"
              data-testid={`onboarding-cta-${item.key}`}
            >
              {item.cta.label} <ArrowSquareOut size={11} weight="duotone" />
            </a>
          )}

          {!done && item.selfAttest && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-[10px] text-white/45 font-sans leading-relaxed mb-2 italic">
                Already finished it in the other tab? Mark it complete so you can keep moving.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onAttest(item)}
                disabled={attesting}
                className={`h-8 text-[10px] uppercase tracking-widest font-sans border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] hover:bg-[#D4AF37]/15 hover:text-[#F3C853] ${opened ? "animate-pulse" : ""}`}
                data-testid={`onboarding-attest-${item.key}`}
              >
                {attesting
                  ? <CircleNotch size={12} className="animate-spin" />
                  : <><CheckCircle size={12} weight="duotone" /> {item.attestLabel}</>}
              </Button>
            </div>
          )}

          {done && (
            <p className="text-[10px] uppercase tracking-widest text-emerald-300/80 font-sans mt-2 flex items-center gap-1.5">
              <CheckCircle size={10} weight="fill" /> Complete
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function OnboardingPanel({ user, compact = false, onRefresh }) {
  const status = user?.agent_status || "pending";
  const onboarding = user?.onboarding || {};
  const completed = ITEMS.filter((i) => onboarding[i.key]).length;
  const total = ITEMS.length;
  const pct = Math.round((completed / total) * 100);
  const [attestingKey, setAttestingKey] = useState(null);

  const handleAttest = async (item) => {
    setAttestingKey(item.key);
    try {
      await api.post("/agent/onboarding/self-attest", { key: item.key });
      toast.success(`${item.title} marked complete`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update — please retry");
    } finally {
      setAttestingKey(null);
    }
  };

  if (status !== "pending" && completed === total && compact) return null;

  const isPending = status === "pending";
  const headline = isPending
    ? "Welcome — let's get you activated"
    : status === "suspended"
      ? "Your account is suspended"
      : "Onboarding progress";
  const subheadline = isPending
    ? "Complete these four items so your admin can activate you."
    : status === "suspended"
      ? "Commission and payouts are paused. Contact your administrator."
      : `You've finished ${completed} of ${total} onboarding items.`;

  return (
    <Card
      className={`bg-gradient-to-br from-[#D4AF37]/10 via-ink-surface to-ink-surface border-[#D4AF37]/30 p-5 sm:p-7 mb-8 ${isPending ? "ring-1 ring-[#D4AF37]/20" : ""}`}
      data-testid="onboarding-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-sans">Onboarding</p>
            <AgentStatusBadge status={status} size="md" data-testid="onboarding-status-badge" />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-white mb-2">{headline}</h2>
          <p className="text-white/60 text-sm font-sans max-w-xl">{subheadline}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Progress</p>
          <p className="font-serif text-3xl text-white" data-testid="onboarding-progress-text">
            {completed}<span className="text-white/40 text-2xl">/{total}</span>
          </p>
          <div className="mt-1.5 w-32 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3C853] transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="onboarding-progress-bar"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="onboarding-checklist">
        {ITEMS.map((item) => (
          <OnboardingTile
            key={item.key}
            item={item}
            done={!!onboarding[item.key]}
            onAttest={handleAttest}
            attesting={attestingKey === item.key}
          />
        ))}
      </div>

      {isPending && completed === total && (
        <p
          className="mt-5 text-xs text-emerald-300 font-sans flex items-center gap-2"
          data-testid="onboarding-ready-banner"
        >
          <CheckCircle size={14} weight="fill" /> All items complete — your admin will activate your account shortly.
        </p>
      )}
    </Card>
  );
}
